export interface PersistedChunkMetadata {
	chunkIndex: number;
	size: number;
}

export type PersistedChunkIntegrityResult =
	| { ok: true }
	| {
			ok: false;
			reason: "incomplete-metadata" | "missing-buffer" | "unverifiable-file-size";
			presentBufferCount: number;
	  }
	| {
			ok: false;
			reason: "size-mismatch";
			presentBufferCount: number;
			chunkIndex: number;
			expectedByteLength: number;
			actualByteLength: number;
	  }
	| {
			ok: false;
			reason: "byte-total-mismatch";
			presentBufferCount: number;
			expectedFileSize: number;
			actualByteTotal: number;
	  };

type BufferReadResult = { ok: true; byteLength: number } | { ok: false; error: unknown };

async function readBuffers(
	chunkIndexes: readonly number[],
	readBuffer: (chunkIndex: number) => Promise<{ byteLength: number } | null | undefined>
): Promise<BufferReadResult[]> {
	return Promise.all(
		chunkIndexes.map(async (chunkIndex) => {
			try {
				const buffer = await readBuffer(chunkIndex);
				return { ok: true, byteLength: buffer?.byteLength ?? 0 } as const;
			} catch (error) {
				return { ok: false, error } as const;
			}
		})
	);
}

export async function inspectPersistedChunkIntegrity(input: {
	chunks: readonly PersistedChunkMetadata[];
	totalChunks: number;
	readBuffer(chunkIndex: number): Promise<{ byteLength: number } | null | undefined>;
	// Optional so existing callers that don't pass it keep their current behavior (no total check) —
	// but every real caller SHOULD pass it: this validator's sibling on the upload path,
	// validatePersistedChunkByteIntegrity (driverChunkLoop.ts), already enforces
	// persistedByteTotal === fileSize, and this reinitialization-gating validator did not, letting a
	// grid where every chunk's buffer matches its OWN metadata row (e.g. persistChunkFromSource
	// recorded the actual bytes read, which can be short of fileSize on a truncated/shrinking source)
	// pass as "guaranteed" reinitializable even though the file total is short (round 50 / LSP-17735).
	fileSize?: number;
}): Promise<PersistedChunkIntegrityResult> {
	const chunksByIndex = new Map(input.chunks.map((chunk) => [chunk.chunkIndex, chunk]));
	const hasExactMetadataCoverage =
		Number.isInteger(input.totalChunks) &&
		input.totalChunks > 0 &&
		input.chunks.length === input.totalChunks &&
		chunksByIndex.size === input.totalChunks &&
		Array.from({ length: input.totalChunks }, (_value, chunkIndex) => chunkIndex).every((chunkIndex) => chunksByIndex.has(chunkIndex));

	if (!hasExactMetadataCoverage) {
		const chunkIndexes = [...chunksByIndex.keys()];
		const bufferReads = await readBuffers(chunkIndexes, input.readBuffer);
		let presentBufferCount = 0;
		for (const bufferRead of bufferReads) {
			if (!bufferRead.ok) throw bufferRead.error;
			if (bufferRead.byteLength > 0) presentBufferCount++;
		}
		return { ok: false, reason: "incomplete-metadata", presentBufferCount };
	}

	const chunkIndexes = Array.from({ length: input.totalChunks }, (_value, chunkIndex) => chunkIndex);
	const bufferReads = await readBuffers(chunkIndexes, input.readBuffer);
	let presentBufferCount = 0;
	let actualByteTotal = 0;
	for (let chunkIndex = 0; chunkIndex < input.totalChunks; chunkIndex++) {
		const chunk = chunksByIndex.get(chunkIndex)!;
		const bufferRead = bufferReads[chunkIndex];
		if (!bufferRead.ok) throw bufferRead.error;
		const actualByteLength = bufferRead.byteLength;
		if (actualByteLength === 0) {
			return { ok: false, reason: "missing-buffer", presentBufferCount };
		}
		presentBufferCount++;
		if (actualByteLength !== chunk.size) {
			return {
				ok: false,
				reason: "size-mismatch",
				presentBufferCount,
				chunkIndex,
				expectedByteLength: chunk.size,
				actualByteLength
			};
		}
		actualByteTotal += actualByteLength;
	}

	if (input.fileSize !== undefined) {
		// A supplied-but-unusable fileSize (NaN, fractional, negative) must fail CLOSED like every other
		// unverifiable condition above — falling through to { ok: true } would claim "guaranteed
		// reinitializable" on evidence we could not actually check (round 50 / LSP-17735). Only an
		// OMITTED fileSize keeps its documented meaning of "caller opted out of the total check".
		if (!Number.isSafeInteger(input.fileSize) || input.fileSize < 0) {
			return { ok: false, reason: "unverifiable-file-size", presentBufferCount };
		}
		if (actualByteTotal !== input.fileSize) {
			return { ok: false, reason: "byte-total-mismatch", presentBufferCount, expectedFileSize: input.fileSize, actualByteTotal };
		}
	}

	return { ok: true };
}
